import { useEffect, useMemo, useState } from "react";
import { X, Eye, EyeOff, ExternalLink, Sun, Moon, Square } from "lucide-react";
import { resolveImageUrl } from "../lib/api";
import { useDominantColor } from "../lib/useDominantColor";
import { useLang } from "../contexts/LanguageContext";

/**
 * Darkroom Discovery — full-screen immersive viewer for a single photo.
 *
 *  • Dim chrome to 10% opacity (revealed on hover) so the photo dominates
 *  • Three backdrop modes (#050505 / #ffffff / #808080) for color/contrast judging
 *  • Floating EXIF overlay you can hide; each EXIF field is a deep-link to a
 *    pre-filtered feed search (uses the indexed exif.iso / aperture / …)
 *  • Beginner tooltip per EXIF label, on hover
 *  • Subtle accent glow derived from the dominant color of the photo,
 *    extracted client-side via a 32×32 canvas downscale (zero dependencies)
 *  • Esc to exit. State driven by ?darkroom=1 search param so a refresh keeps
 *    the user where they were, and the URL is shareable.
 */

const BACKDROPS = [
  { key: "dark", value: "#050505", icon: Moon, label: "Dark" },
  { key: "grey", value: "#808080", icon: Square, label: "Grey" },
  { key: "light", value: "#ffffff", icon: Sun, label: "Light" },
];

// Each field renders as a link to /?<facet>=<value>. The tooltip is a short
// novice-friendly explanation — shown on hover, lives only in this component.
const EXIF_FIELDS = [
  { key: "iso", t_key: "iso", tip_key: "tip_iso", facet: "iso" },
  { key: "aperture", t_key: "aperture", tip_key: "tip_aperture", facet: "aperture" },
  { key: "shutter", t_key: "shutter", tip_key: "tip_shutter", facet: "shutter" },
  { key: "focal_length", t_key: "focal_length", tip_key: "tip_focal_length", facet: "focal_length" },
  { key: "lens", t_key: "lens", tip_key: "tip_lens", facet: "lens" },
];

function ExifLink({ field, value, onNavigate, accent, t }) {
  const [hover, setHover] = useState(false);
  if (!value) return null;
  const tip = t(field.tip_key);
  return (
    <div className="relative" data-testid={`darkroom-exif-${field.key}`}>
      <button
        onClick={() => onNavigate(field.facet, value)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="group w-full text-left border border-white/10 hover:border-white/40 px-4 py-3 transition-all bg-white/[0.02]"
        style={hover ? { boxShadow: `0 0 0 1px ${accent || "rgba(220,38,38,0.7)"}` } : undefined}
        data-testid={`darkroom-exif-link-${field.key}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">
            {t(field.t_key)}
          </div>
          <ExternalLink size={11} className="text-white/30 group-hover:text-white/70 transition-colors" />
        </div>
        <div
          className="font-mono text-sm text-white/90 mt-1 break-words transition-colors"
          style={hover ? { color: accent || undefined } : undefined}
        >
          {value}
        </div>
      </button>
      {hover && (
        <div
          className="absolute z-50 right-0 top-[calc(100%+6px)] w-64 bg-black/90 border border-white/15 px-3 py-2 text-[11px] font-light leading-relaxed text-white/80 pointer-events-none"
          data-testid={`darkroom-tip-${field.key}`}
        >
          {tip}
        </div>
      )}
    </div>
  );
}

export default function DarkroomViewer({ photo, onExit }) {
  const { t } = useLang();
  const [backdropIdx, setBackdropIdx] = useState(0);
  const [showOverlay, setShowOverlay] = useState(true);
  const [chromeRevealed, setChromeRevealed] = useState(false);
  const accent = useDominantColor(resolveImageUrl(photo?.image_url));
  const backdrop = BACKDROPS[backdropIdx];
  const isLight = backdrop.key === "light";
  const chromeColor = isLight ? "rgba(15,15,15," : "rgba(255,255,255,";
  const chromeOpacity = chromeRevealed ? "0.95" : "0.10";

  // Esc to exit. Body scroll lock so the rest of the page doesn't move.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onExit();
      else if (e.key.toLowerCase() === "i") setShowOverlay((v) => !v);
      else if (e.key.toLowerCase() === "b") setBackdropIdx((i) => (i + 1) % BACKDROPS.length);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onExit]);

  const navigateToFacet = (facet, value) => {
    const usp = new URLSearchParams();
    usp.set(facet, String(value));
    const url = `/?${usp.toString()}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const exifEntries = useMemo(() => {
    if (!photo?.exif) return [];
    return EXIF_FIELDS.filter((f) => Boolean(photo.exif[f.key]));
  }, [photo]);

  const cameraLabel = photo?.exif
    ? [photo.exif.camera_make, photo.exif.camera_model].filter(Boolean).join(" ")
    : "";

  const overlayActive = showOverlay && exifEntries.length > 0;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-[padding,background-color] duration-500 ease-out ${
        overlayActive ? "lg:pr-[360px]" : ""
      }`}
      style={{ backgroundColor: backdrop.value }}
      onMouseMove={() => {
        if (!chromeRevealed) setChromeRevealed(true);
      }}
      onMouseLeave={() => setChromeRevealed(false)}
      data-testid="darkroom-overlay"
    >
      {/* The frame */}
      <img
        src={resolveImageUrl(photo.image_url)}
        alt={photo.title}
        className="max-w-[92vw] max-h-[92vh] object-contain select-none transition-all duration-500 ease-out"
        draggable={false}
        data-testid="darkroom-photo"
      />

      {/* Top bar — title + exit */}
      <div
        className="absolute top-0 inset-x-0 flex items-center justify-between px-6 py-5 transition-opacity duration-300"
        style={{
          opacity: chromeOpacity,
          color: `${chromeColor}1)`,
        }}
      >
        <div className="min-w-0 flex-1 pr-8">
          <div
            className="font-mono text-[10px] uppercase tracking-[0.3em]"
            style={{ color: `${chromeColor}0.6)` }}
          >
            {t("darkroom_mode")}
          </div>
          <div
            className="font-serif text-xl mt-1 font-light truncate"
            style={{ color: `${chromeColor}1)` }}
          >
            {photo.title}
          </div>
          {cameraLabel && (
            <div
              className="font-mono text-[10px] uppercase tracking-[0.25em] mt-0.5"
              style={{ color: `${chromeColor}0.55)` }}
            >
              {cameraLabel}
            </div>
          )}
        </div>
        <button
          onClick={onExit}
          className="border px-3 py-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] transition-colors"
          style={{
            borderColor: `${chromeColor}0.25)`,
            color: `${chromeColor}0.85)`,
          }}
          data-testid="darkroom-exit-button"
          aria-label={t("darkroom_exit")}
        >
          <X size={12} /> ESC
        </button>
      </div>

      {/* Bottom-left controls — backdrop toggle + overlay toggle */}
      <div
        className="absolute bottom-0 left-0 flex items-center gap-3 px-6 py-5 transition-opacity duration-300"
        style={{ opacity: chromeOpacity }}
      >
        <div
          className="flex items-center border"
          style={{ borderColor: `${chromeColor}0.25)` }}
          data-testid="darkroom-backdrop-toggle"
        >
          {BACKDROPS.map((b, i) => {
            const Icon = b.icon;
            const active = i === backdropIdx;
            return (
              <button
                key={b.key}
                onClick={() => setBackdropIdx(i)}
                className="px-3 py-2 transition-colors"
                style={{
                  backgroundColor: active ? `${chromeColor}0.85)` : "transparent",
                  color: active ? backdrop.value : `${chromeColor}0.75)`,
                }}
                title={t(`backdrop_${b.key}`)}
                aria-label={t(`backdrop_${b.key}`)}
                data-testid={`darkroom-backdrop-${b.key}`}
              >
                <Icon size={13} />
              </button>
            );
          })}
        </div>
        <button
          onClick={() => setShowOverlay((v) => !v)}
          className="border px-3 py-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] transition-colors"
          style={{
            borderColor: `${chromeColor}0.25)`,
            color: `${chromeColor}0.85)`,
          }}
          data-testid="darkroom-overlay-toggle"
          aria-pressed={showOverlay}
        >
          {showOverlay ? <EyeOff size={12} /> : <Eye size={12} />}
          {showOverlay ? t("darkroom_hide_exif") : t("darkroom_show_exif")}
        </button>
      </div>

      {/* Floating EXIF overlay — bottom-right */}
      {overlayActive && (
        <div
          className="absolute bottom-6 right-6 w-[320px] backdrop-blur-md bg-black/55 border border-white/10 p-4 transition-opacity duration-200"
          style={{
            boxShadow: accent ? `0 0 32px -8px ${accent}` : undefined,
          }}
          data-testid="darkroom-exif-overlay"
        >
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
              {t("shot_details")}
            </div>
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: accent || "rgb(220,38,38)" }}
              title={t("dominant_color")}
              data-testid="darkroom-accent-dot"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {exifEntries.map((field) => (
              <ExifLink
                key={field.key}
                field={field}
                value={photo.exif[field.key]}
                onNavigate={navigateToFacet}
                accent={accent}
                t={t}
              />
            ))}
          </div>
          {photo.exif.camera_model && (
            <div className="mt-3 pt-3 border-t border-white/10 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
              {t("hint_click_to_search")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
